import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  listPromptsAction,
  listLlms,
  listPromptScreens,
} from '../../../../../store/actions/promptsv2.action';
import { getAllConditionListAction } from '../../../../../store/actions/condition.action';
import { getAllSpecialtyListAction } from '../../../../../store/actions/specialty.action';
import { Spin, Dropdown, Menu } from 'antd';
import { PlusIcon, ActionMenuIcon, EditAppointmentIcon } from 'src/icons';
import { Button } from '../../../../baseComponents';
import { SuperAdminTable } from '../../../../baseComponents/Table';
import ListPromptsFilters from './ListPromptsFilters';
import EditPromptModal from './EditPromptModal';
import CreatePromptModal from './CreatePromptModal';
import organizationEventEmitter from '../../../../../../../utilities/organizationEmmiter';
import { getFromStorage } from '../../../../../../../lib/storage';
import { getAllActiveOrganizationAction } from 'src/modules/SuperAdmin/store/actions/organization.action';

const ListPrompts = () => {
  const dispatch = useDispatch();
  const {
    prompts,
    promptsLoading,
    llms,
    llmsLoading,
    promptScreens,
    promptScreensLoading,
  } = useSelector((state) => state?.promptsState);
  const {
    conditions,
    conditionsLoading,
    specialties,
    specialtiesLoading,
    currentPractitioner,
    serviceRequestTypes,
  } = useSelector((state) => ({
    conditions: state?.conditionState?.conditionsDropdown,
    conditionsLoading: state?.conditionState?.conditionsLoading,
    specialties: state?.specialtyState?.specialtiesDropdown,
    specialtiesLoading: state?.specialtyState?.specialtiesLoading,
    currentPractitioner: state?.practitionerState?.currentPractitioner,
    serviceRequestTypes: state?.serviceRequestState?.srTypeList,
  }));
  const [filters, setFilters] = useState({ page: 1, per_page: 20 });

  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [editingPromptId, setEditingPromptId] = useState(null);
  const [selectedOrganizationUUID, setSelectedOrganizationUUID] = useState(
    () => {
      const savedOrg = getFromStorage('superAdminSelectedOrganization');
      const parsedSavedOrg = savedOrg ? JSON.parse(savedOrg) : null;
      return parsedSavedOrg?.org_uuid || currentPractitioner?.org_uuid || null;
    }
  );

  useEffect(() => {
    dispatch(listLlms());
    dispatch(listPromptScreens(filters?.condition_id, filters?.specialty_id));
    dispatch(getAllConditionListAction());
    dispatch(getAllSpecialtyListAction());
  }, [dispatch, filters]);

  useEffect(() => {
    dispatch(listPromptsAction(filters));
  }, [dispatch, filters]);

  useEffect(() => {
    if (selectedOrganizationUUID) {
      dispatch(getAllActiveOrganizationAction());
    }
  }, [dispatch, selectedOrganizationUUID]);

  const handleEdit = (promptId) => {
    setIsEditModalVisible(true);
    setEditingPromptId(promptId);
  };

  const menuOptions = (id) => {
    return (
      <Menu
        onClick={({ key }) => {
          if (key === 'editPrompt') {
            handleEdit(id);
          }
        }}
      >
        <Menu.Item key="editPrompt">
          <div className="dropdown-text flex items-center space-x-2">
            <EditAppointmentIcon /> <span>Edit Prompt</span>
          </div>
        </Menu.Item>
      </Menu>
    );
  };

  const columns = [
    {
      title: <span className="font-[700]">Prompt</span>,
      dataIndex: 'prompt',
      key: 'prompt',
      render: (text) =>
        text && text.length > 50 ? `${text.substring(0, 50)}...` : text,
    },
    {
      title: <span className="font-[700]">Version</span>,
      dataIndex: 'version',
      key: 'version',
    },
    {
      title: <span className="font-[700]">LLM</span>,
      dataIndex: 'llm_model',
      key: 'llm_model',
    },
    {
      title: <span className="font-[700]">Condition</span>,
      dataIndex: 'condition_name',
      key: 'condition_name',
    },
    {
      title: <span className="font-[700]">Service Request Type</span>,
      key: 'service_request_type',
      render: (text, record) => {
        return serviceRequestTypes?.find(
          (srType) =>
            srType?.service_request_type_id === record?.service_request_type_id
        )?.service_request_type;
      },
    },
    {
      title: <span className="font-[700]">Agent</span>,
      dataIndex: 'agent_name',
      key: 'agent_name',
      render: (text) => text || 'N/A',
    },
    {
      title: <span className="font-[700]">Prompt Screen</span>,
      dataIndex: 'prompt_screens_name',
      key: 'prompt_screens_name',
    },
    {
      title: 'Actions',
      key: 'actions',
      dataIndex: 'actions',
      render: (text, record) => {
        return (
          <Dropdown
            className=""
            overlay={menuOptions(record?.prompt_id)}
            trigger={['click']}
          >
            <a className="" onClick={(e) => e.preventDefault()}>
              <ActionMenuIcon />
            </a>
          </Dropdown>
        );
      },
    },
  ];

  const tableLoading =
    promptsLoading ||
    llmsLoading ||
    promptScreensLoading ||
    conditionsLoading ||
    specialtiesLoading;

  const handleTableChange = (pagination) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      page: pagination.current,
      per_page: pagination.pageSize,
    }));
  };

  useEffect(() => {
    const unsubscribe = organizationEventEmitter.subscribe(
      'organizationChanged',
      (newOrg) => {
        setSelectedOrganizationUUID(newOrg?.org_uuid);
      }
    );
    return () => unsubscribe();
  }, []);

  return (
    <div>
      <div className="flex flex-col sm:flex-row lg:justify-between justify-center items-center mb-4">
        <div className="flex flex-col sm:flex-row">
          <ListPromptsFilters
            conditions={conditions}
            conditionsLoading={conditionsLoading}
            promptScreens={promptScreens}
            promptScreensLoading={promptScreensLoading}
            specialties={specialties}
            specialtiesLoading={specialtiesLoading}
            filters={filters}
            setFilters={setFilters}
            currentPage={filters.page}
            pageSize={filters.per_page}
          />
        </div>
        <div className="flex justify-end items-end">
          <Button type={'button'} onClick={() => setIsCreateModalVisible(true)}>
            <PlusIcon className={`mr-2`} /> Add New Prompt
          </Button>
        </div>
      </div>

      <div className="mt-5 ant-table rounded-xl shadow-xl">
        <SuperAdminTable
          className={'appointment-page-table'}
          data={prompts?.prompts}
          columns={columns}
          loading={promptsLoading}
          rowKey="prompt_id"
          pagination={{
            total: prompts?.total_items,
            pageSize: prompts?.per_page,
            current: prompts?.page,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50'],
          }}
          onChange={handleTableChange}
        />
      </div>

      <EditPromptModal
        visible={isEditModalVisible}
        setVisible={setIsEditModalVisible}
        promptId={editingPromptId}
        filters={filters}
        selectedOrganizationUUID={selectedOrganizationUUID}
      />
      <CreatePromptModal
        visible={isCreateModalVisible}
        setVisible={setIsCreateModalVisible}
        filters={filters}
        selectedOrganizationUUID={selectedOrganizationUUID}
      />
    </div>
  );
};

export default ListPrompts;
